import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Tag, Download, Star, PackageSearch } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/cart/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = ({ sessionId }) => {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!name.trim() || !text.trim()) return;
    try {
      await axios.post(`${API}/reviews`, { session_id: sessionId, name: name.trim(), rating, text: text.trim() });
      setDone(true);
      toast.success("Review sent — it appears after approval");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save review");
    }
  };

  if (done) {
    return (
      <p data-testid="review-thanks" className="f-mono text-xs text-[#39ff14] uppercase tracking-wider text-center py-2">
        Review received — thank you!
      </p>
    );
  }
  return (
    <div className="bg-[#0a0a0c] border border-[#22242a] p-4 space-y-3" data-testid="review-form">
      <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-[0.2em]">Leave a review</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setRating(n)} aria-label={`${n} stars`}>
            <Star size={20} className={n <= rating ? "text-[#39ff14] fill-[#39ff14]" : "text-[#22242a]"} strokeWidth={1.5} />
          </button>
        ))}
      </div>
      <input
        data-testid="review-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        className="w-full bg-[#121316] border border-[#22242a] px-3 py-2 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
      />
      <textarea
        data-testid="review-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        maxLength={500}
        placeholder="How was it?"
        className="w-full bg-[#121316] border border-[#22242a] px-3 py-2 text-xs f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14] resize-none"
      />
      <button
        data-testid="review-submit-button"
        onClick={submit}
        className="w-full py-2.5 bg-[#22242a] text-white f-mono text-[11px] uppercase tracking-[0.14em] font-bold hover:bg-[#39ff14] hover:text-black transition-colors"
      >
        Submit Review
      </button>
    </div>
  );
};

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* keep polling briefly */
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="bg-[#0a0a0c] text-[#ecebe4] min-h-screen flex flex-col">
      <nav className="border-b border-[#22242a] px-6 py-4">
        <span className="flex items-center gap-3">
          <img src="/logo.png" alt="The Print Plug logo" className="w-10 h-10 object-contain" />
          <span className="f-disp tracking-wider text-xl uppercase leading-none text-white">
            THE PRINT PLUG<span className="text-[#39ff14]">.</span>
          </span>
        </span>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div className="max-w-lg w-full bg-[#121316] border border-[#22242a] p-8 md:p-12 space-y-6" data-testid="payment-success-panel">
          {status === "polling" && (
            <div className="text-center space-y-4" data-testid="payment-polling-state">
              <Loader2 size={40} className="mx-auto text-[#39ff14] animate-spin" />
              <h1 className="f-disp text-3xl uppercase text-white">Confirming Payment…</h1>
              <p className="f-body text-sm text-[#8e929b]">Checking with Stripe. One second.</p>
            </div>
          )}

          {status === "paid" && (
            <div className="space-y-6" data-testid="payment-paid-state">
              <CheckCircle2 size={40} className="text-[#39ff14]" />
              <h1 className="f-disp text-4xl uppercase text-white leading-[0.95]">
                Payment Locked In.
              </h1>
              <p className="f-body text-sm text-[#8e929b] leading-relaxed">
                Your order is confirmed and a pre-filled USPS shipping label is being generated for dispatch.
              </p>
              {order && (
                <div className="bg-[#0a0a0c] border border-[#22242a] p-4 space-y-3">
                  {order.order_number && (
                    <div className="flex justify-between items-center f-mono border-b border-[#22242a] pb-3">
                      <span className="text-[#8e929b] text-xs uppercase tracking-wider">Order number</span>
                      <span data-testid="order-number" className="text-[#39ff14] text-lg font-bold">{order.order_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between f-mono text-xs uppercase tracking-wider">
                    <span className="text-[#8e929b]">Order</span>
                    <span data-testid="order-id" className="text-white truncate max-w-[200px]">{order.order_id}</span>
                  </div>
                  {order.items?.map((i) => (
                    <div key={i.variant_id || i.product_id} className="space-y-2">
                      <div className="flex justify-between f-mono text-xs">
                        <span className="text-[#ecebe4]">{i.name} × {i.qty}</span>
                        <span className="text-[#39ff14] tabular-nums">
                          ${((i.unit_price_cents * i.qty) / 100).toFixed(2)}
                        </span>
                      </div>
                      {i.has_digital && (
                        <a
                          data-testid={`download-${i.variant_id}`}
                          href={`${API}/orders/${sessionId}/download/${i.variant_id}`}
                          className="flex items-center justify-center gap-2 f-mono text-[11px] uppercase tracking-[0.14em] px-4 py-3 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors"
                        >
                          <Download size={13} strokeWidth={2.5} />
                          Download {i.name}
                        </a>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-between f-mono text-xs uppercase tracking-wider border-t border-[#22242a] pt-3">
                    <span className="text-[#8e929b] flex items-center gap-2"><Tag size={12} className="text-[#39ff14]" /> Shipping Label</span>
                    <span data-testid="label-status" className="text-[#39ff14]">
                      {order.label_status === "created"
                        ? "USPS Label Created"
                        : order.label_status === "digital_delivery"
                        ? "Instant Digital Delivery"
                        : "Pre-Filled Label Queued"}
                    </span>
                  </div>
                  {order.label_status === "created" && (
                    <div className="space-y-2 border-t border-[#22242a] pt-3" data-testid="label-details">
                      {order.tracking_number && (
                        <div className="flex justify-between f-mono text-xs">
                          <span className="text-[#8e929b] uppercase tracking-wider">Tracking</span>
                          <a
                            data-testid="tracking-link"
                            href={order.tracking_url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#39ff14] hover:text-white transition-colors"
                          >
                            {order.tracking_number}
                          </a>
                        </div>
                      )}
                      {order.label_url && (
                        <a
                          data-testid="label-pdf-link"
                          href={order.label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-center f-mono text-xs uppercase tracking-[0.14em] px-4 py-3 bg-[#22242a] text-white font-bold hover:bg-[#39ff14] hover:text-black transition-colors"
                        >
                          View USPS Label PDF
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
              {order && <ReviewForm sessionId={sessionId} />}
              <div className="flex flex-col gap-3">
                <Link
                  to="/track"
                  data-testid="success-track-link"
                  className="flex items-center justify-center gap-2 f-mono text-xs uppercase tracking-[0.14em] px-8 py-4 bg-[#22242a] text-white font-bold hover:bg-[#39ff14] hover:text-black transition-colors"
                >
                  <PackageSearch size={14} strokeWidth={2.5} />
                  Track My Order Anytime
                </Link>
                <Link
                  to="/"
                  data-testid="success-back-to-store"
                  className="block text-center f-mono text-xs uppercase tracking-[0.14em] px-8 py-4 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors d1-glow"
                >
                  Back to the Plug
                </Link>
              </div>
            </div>
          )}

          {(status === "pending" || status === "error") && (
            <div className="space-y-6" data-testid="payment-pending-state">
              <h1 className="f-disp text-3xl uppercase text-white leading-[0.95]">
                Payment Processing.
              </h1>
              <p className="f-body text-sm text-[#8e929b] leading-relaxed">
                Stripe is still confirming this payment. If you were charged, your order will appear shortly — check your email for the Stripe receipt.
              </p>
              <Link
                to="/"
                data-testid="pending-back-to-store"
                className="block text-center f-mono text-xs uppercase tracking-[0.14em] px-8 py-4 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors d1-glow"
              >
                Back to the Plug
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
